import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiError } from "./api";
import { addToCart, getCart, removeFromCart, type CartPayload } from "./store.functions";

/**
 * Cart state for the research store. The cart itself lives in Shopify; all we
 * keep is its id, in localStorage, so a reload (or a return trip from Shopify's
 * hosted checkout) restores the same cart. Checkout is Shopify's — no payment
 * data ever touches this app.
 */

const STORAGE_KEY = "mii-cart-v1";

type CartContextValue = {
  cart: CartPayload | null;
  count: number;
  isLoading: boolean;
  addItem: (variantId: string) => void;
  adding: boolean;
  removeItem: (lineId: string) => void;
  removing: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
};

const CartCtx = createContext<CartContextValue>({
  cart: null,
  count: 0,
  isLoading: false,
  addItem: () => {},
  adding: false,
  removeItem: () => {},
  removing: false,
  open: false,
  setOpen: () => {},
});

function readStoredCartId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredCartId(id: string | null) {
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY, id);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage blocked (private mode / quota) — the cart still works this session
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [cartId, setCartId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setCartId(readStoredCartId());
  }, []);

  const cartQuery = useQuery({
    queryKey: ["cart", cartId],
    queryFn: () => getCart({ data: { cartId: cartId! } }),
    enabled: !!cartId,
    retry: false,
  });

  /**
   * Carts expire on Shopify's side. A dead id is not an error the visitor needs
   * to see — drop it and start clean; the next add creates a new cart.
   */
  useEffect(() => {
    const err = cartQuery.error;
    if (err instanceof ApiError && err.code === "CART_NOT_FOUND") {
      writeStoredCartId(null);
      setCartId(null);
    }
  }, [cartQuery.error]);

  const persist = useCallback(
    (payload: CartPayload) => {
      writeStoredCartId(payload.cartId);
      setCartId(payload.cartId);
      qc.setQueryData(["cart", payload.cartId], payload);
    },
    [qc],
  );

  const addMut = useMutation({
    mutationFn: (variantId: string) =>
      addToCart({ data: { cartId: cartId ?? undefined, variantId } }),
    onSuccess: (payload) => {
      persist(payload);
      if (payload.alreadyInCart) toast.info("Already in your cart");
      else toast.success("Added to cart");
      setOpen(true);
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Could not add that report to your cart"),
  });

  const removeMut = useMutation({
    mutationFn: (lineId: string) => removeFromCart({ data: { cartId: cartId!, lineId } }),
    onSuccess: (payload) => {
      persist(payload);
      toast.success("Removed from cart");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not remove that item"),
  });

  const cart = cartQuery.data ?? null;

  const value: CartContextValue = {
    cart,
    count: cart?.lines.length ?? 0,
    isLoading: cartQuery.isLoading,
    addItem: (variantId: string) => addMut.mutate(variantId),
    adding: addMut.isPending,
    removeItem: (lineId: string) => removeMut.mutate(lineId),
    removing: removeMut.isPending,
    open,
    setOpen,
  };

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart() {
  return useContext(CartCtx);
}
